'use memo'

import {
  CampaignDetailModelState,
  Deposit,
  DepositType,
  Condition,
  ConditionType,
  DayType,
  FormCondition,
  RenderedSpecialCondition,
  TimeUnit,
  StaffGetContractDatesResponse,
  useStaffGetContractDates,
  useStaffGetSpecialConditions,
} from '@urbanx/agx.campaigns.client'
import { useSelectedCampaign, useSelectedContract } from 'hooks'
import { useCallback, useMemo } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { useBusinessDate } from 'hooks/useBusinessDate'
import { dateOnlyToDate } from 'utils/date'
import { getNswBalanceDepositDueDate } from '../depositCalculations'

type ContractDatesForDueDate = Omit<
  StaffGetContractDatesResponse,
  'contractDate' | 'expectedUnconditionalDate'
> & {
  contractDate?: StaffGetContractDatesResponse['contractDate'] | Date
  expectedUnconditionalDate?:
    | StaffGetContractDatesResponse['expectedUnconditionalDate']
    | Date
}

type DateOnlyValue =
  | StaffGetContractDatesResponse['contractDate']
  | Date
  | null
  | undefined

type FormConditionDueDateResult = {
  dueDate: Date | null
  dueDateString: string
  isPending: boolean
  isError: boolean
}

const toDate = (
  value: DateOnlyValue
): Date | undefined => {
  if (!value) return undefined
  if (value instanceof Date) return value

  return dateOnlyToDate(value) ?? undefined
}

const toDayjs = (value: DateOnlyValue): Dayjs | null => {
  const dateValue = toDate(value)
  if (!dateValue) return null

  const parsed = dayjs(dateValue)
  return parsed.isValid() ? parsed : null
}

const toDueDateResult = (
  dueDate: Dayjs | null,
  isPending: boolean,
  isError: boolean
): FormConditionDueDateResult => ({
  dueDate: dueDate?.toDate() ?? null,
  dueDateString: dueDate?.format('YYYY-MM-DD') ?? '',
  isPending,
  isError,
})

const toDateOnlyDueDateResult = (
  dateOnly: string | null | undefined,
  isPending: boolean,
  isError: boolean
): FormConditionDueDateResult => ({
  dueDate: toDate(dateOnly) ?? null,
  dueDateString: dateOnly ?? '',
  isPending,
  isError,
})

const getBaseDateFromConditionType = (
  conditionType: ConditionType | undefined,
  contractDates: ContractDatesForDueDate | undefined
): Dayjs | null => {
  if (!contractDates) return null

  let dateValue: Date | undefined

  switch (conditionType) {
    case ConditionType.Contract:
      dateValue = toDate(contractDates.contractDate)
      break
    case ConditionType.Finance:
      dateValue = toDate(contractDates.subjectToFinance?.referenceDate)
      break
    case ConditionType.Unconditional:
      dateValue = toDate(contractDates.expectedUnconditionalDate)
      break
  }

  if (!dateValue) return null

  return toDayjs(dateValue)
}

// Helper to calculate a condition's due date from its own settings
const calculateConditionDueDate = (
  formCondition: FormCondition | undefined,
  contractDates: ContractDatesForDueDate | undefined,
  addTimeUnit: (
    baseDate: Dayjs,
    amount: number,
    unit: TimeUnit | undefined,
    isBusinessDays: boolean
  ) => Dayjs
): Dayjs | null => {
  if (!formCondition || !contractDates) return null

  const conditionValue = formCondition.condition

  // If it's a specific date, use referenceDate directly
  if (conditionValue === Condition.OnSpecificDate) {
    if (formCondition.referenceDate) {
      return toDayjs(formCondition.referenceDate)
    }
    return null
  }

  // If it's N days from a date (contract date, etc.)
  if (conditionValue === Condition.NdaysFromDate) {
    const baseDate = getBaseDateFromConditionType(
      formCondition.conditionType as ConditionType,
      contractDates
    )

    if (!baseDate) return null

    // If no days specified, use base date directly
    if (
      formCondition.daysFromDate === undefined ||
      formCondition.daysFromDate === 0
    ) {
      return baseDate
    }

    const isBusinessDays = formCondition.dayType === DayType.BusinessDays

    return addTimeUnit(
      baseDate,
      formCondition.daysFromDate,
      formCondition.fromDateTimeUnit,
      isBusinessDays
    )
  }

  // Fallback to referenceDate if available
  if (formCondition.referenceDate) {
    return toDayjs(formCondition.referenceDate)
  }

  return null
}

const getBaseDateFromCondition = (
  conditionValue: Condition | undefined,
  contractDates: ContractDatesForDueDate | undefined,
  addTimeUnit: (
    baseDate: Dayjs,
    amount: number,
    unit: TimeUnit | undefined,
    isBusinessDays: boolean
  ) => Dayjs
): Dayjs | null => {
  if (!contractDates) return null

  let formCondition: FormCondition | undefined

  switch (conditionValue) {
    case Condition.FinanceBeingSatisfied:
      formCondition = contractDates.subjectToFinance
      break
    case Condition.BuildingAndPestBeingSatisfied:
      formCondition = contractDates.buildingAndPestCondition
      break
    case Condition.UponSatisfactionOfSpecialCondition:
      // Special conditions might have their own reference date
      // This would need to be passed through the condition object
      return null
    default:
      return null
  }

  if (!formCondition) return null

  // Calculate the actual due date of the finance/building & pest condition
  return calculateConditionDueDate(formCondition, contractDates, addTimeUnit)
}

const getSpecialConditionDueDate = (
  renderedSpecialConditions: RenderedSpecialCondition[] | undefined,
  specialConditionId: string | null | undefined
): Dayjs | null => {
  if (!renderedSpecialConditions || !specialConditionId) return null

  for (const specialCondition of renderedSpecialConditions) {
    if (
      specialCondition.id === specialConditionId &&
      specialCondition.dueDate
    ) {
      return toDayjs(specialCondition.dueDate)
    }

    const matchingClause = specialCondition.clauses?.find(
      (clause) => clause.clauseId === specialConditionId
    )

    if (matchingClause?.dueDate) {
      return toDayjs(matchingClause.dueDate)
    }
  }

  return null
}

export const useFormConditionDueDate = (
  condition: FormCondition | Deposit | undefined,
  contractDateOverride?: DateOnlyValue
) => {
  const { campaign } = useSelectedCampaign()
  const { contract, campaignId } = useSelectedContract()
  const { moveToNextBusinessDay, addBusinessDays } = useBusinessDate(
    campaign?.state || ''
  )
  const specialConditionId =
    condition?.condition === Condition.UponSatisfactionOfSpecialCondition
      ? condition.specialConditionId
      : undefined
  const {
    data: specialConditions,
    isPending: specialConditionsIsPending,
    isError: specialConditionsIsError,
  } = useStaffGetSpecialConditions(
    campaignId || '',
    contract?.contractId || '',
    !!campaignId && !!contract?.contractId && !!specialConditionId
  )
  const {
    data: contractDates,
    isPending: contractDatesIsPending,
    isError: contractDatesIsError,
  } = useStaffGetContractDates(
    campaignId || '',
    contract?.contractId || '',
    !!campaignId && !!contract?.contractId
  )

  const isPending =
    (!!specialConditionId && specialConditionsIsPending) ||
    contractDatesIsPending
  const isError =
    (!!specialConditionId && specialConditionsIsError) || contractDatesIsError

  const addTimeUnit = useCallback(
    (
      baseDate: Dayjs,
      amount: number,
      unit: TimeUnit | undefined,
      isBusinessDays: boolean
    ): Dayjs => {
      // If business days and unit is Days or undefined (default to days), use addBusinessDays
      if (isBusinessDays && (unit === TimeUnit.Days || unit === undefined)) {
        return addBusinessDays(baseDate, amount)
      }

      switch (unit) {
        case TimeUnit.Days:
          return baseDate.add(amount, 'day')
        case TimeUnit.Weeks:
          return baseDate.add(amount, 'week')
        case TimeUnit.Months:
          return baseDate.add(amount, 'month')
        case TimeUnit.Years:
          return baseDate.add(amount, 'year')
        default:
          return baseDate.add(amount, 'day')
      }
    },
    [addBusinessDays]
  )

  return useMemo(() => {
    if (isError || !condition) {
      return toDueDateResult(null, isPending, isError)
    }

    // If the caller has a local contract date, keep using the API response for
    // linked conditions but fill an empty API contractDate from the override.
    const effectiveContractDates =
      (contractDates || contractDateOverride)
        ? {
            ...contractDates,
            contractDate: contractDates?.contractDate ?? contractDateOverride ?? undefined,
          }
        : undefined

    if (!effectiveContractDates && isPending) {
      return toDueDateResult(null, isPending, isError)
    }

    const conditionValue = condition.condition

    // Handle "OnDate" - use referenceDate or look up based on conditionType
    if (conditionValue === Condition.OnDate) {
      const depositCondition = condition as Deposit
      let refDate: Dayjs | null = null
      const shouldUseNswBalanceDepositDate =
        campaign?.state === CampaignDetailModelState.NSW &&
        depositCondition.depositType === DepositType.Balance &&
        (depositCondition.conditionType === undefined ||
          depositCondition.conditionType === ConditionType.Contract ||
          depositCondition.conditionType === ConditionType.Unconditional)

      if (shouldUseNswBalanceDepositDate) {
        const dueDate = getNswBalanceDepositDueDate({
          contractDate: toDate(effectiveContractDates?.contractDate),
          coolingOffDetails: effectiveContractDates?.coolingOffDetails,
          addBusinessDays,
          moveToNextBusinessDay,
        })

        if (dueDate) {
          return toDueDateResult(dayjs(dueDate), isPending, isError)
        }
      }

      // First try to use explicit referenceDate if provided
      if (condition.referenceDate) {
        refDate = toDayjs(condition.referenceDate)
        if (refDate) {
          if (
            depositCondition.conditionType === undefined ||
            depositCondition.conditionType === ConditionType.Contract
          ) {
            refDate = moveToNextBusinessDay(refDate)
          }
          return toDueDateResult(refDate, isPending, isError)
        }
        return toDueDateResult(null, isPending, isError)
      }

      // If no referenceDate, look up based on conditionType
      if (!refDate && depositCondition.conditionType !== undefined) {
        refDate = getBaseDateFromConditionType(
          depositCondition.conditionType,
          effectiveContractDates
        )
      }

      if (refDate) {
        if (
          depositCondition.conditionType === undefined ||
          depositCondition.conditionType === ConditionType.Contract
        ) {
          refDate = moveToNextBusinessDay(refDate)
        }
        return toDueDateResult(refDate, isPending, isError)
      }
    }

    // If it's a specific date, use referenceDate directly
    if (conditionValue === Condition.OnSpecificDate) {
      if (condition.referenceDate) {
        return toDateOnlyDueDateResult(
          condition.referenceDate,
          isPending,
          isError
        )
      }
    }

    // Handle "FinanceBeingSatisfied" - use finance condition reference date
    if (conditionValue === Condition.FinanceBeingSatisfied) {
      const baseDate = getBaseDateFromCondition(
        conditionValue,
        effectiveContractDates,
        addTimeUnit
      )
      if (baseDate) {
        // If there are additional days to add
        if (
          condition.daysFromDate !== undefined &&
          condition.daysFromDate > 0
        ) {
          const calculatedDate = addTimeUnit(
            baseDate,
            condition.daysFromDate,
            condition.fromDateTimeUnit,
            condition.dayType === DayType.BusinessDays
          )
          return toDueDateResult(
            moveToNextBusinessDay(calculatedDate),
            isPending,
            isError
          )
        }
        return toDueDateResult(
          moveToNextBusinessDay(baseDate),
          isPending,
          isError
        )
      }
    }

    // Handle "BuildingAndPestBeingSatisfied" - use building and pest condition reference date
    if (conditionValue === Condition.BuildingAndPestBeingSatisfied) {
      const baseDate = getBaseDateFromCondition(
        conditionValue,
        effectiveContractDates,
        addTimeUnit
      )
      if (baseDate) {
        // If there are additional days to add
        if (
          condition.daysFromDate !== undefined &&
          condition.daysFromDate > 0
        ) {
          const calculatedDate = addTimeUnit(
            baseDate,
            condition.daysFromDate,
            condition.fromDateTimeUnit,
            condition.dayType === DayType.BusinessDays
          )
          return toDueDateResult(
            moveToNextBusinessDay(calculatedDate),
            isPending,
            isError
          )
        }
        return toDueDateResult(
          moveToNextBusinessDay(baseDate),
          isPending,
          isError
        )
      }
    }

    // Handle "UponSatisfactionOfSpecialCondition"
    if (conditionValue === Condition.UponSatisfactionOfSpecialCondition) {
      let baseDate: Dayjs | null = null
      const referenceDate = condition.referenceDate

      // If the condition has a referenceDate (pre-computed or manually set), use it
      if (referenceDate) {
        baseDate = toDayjs(referenceDate)
      }

      if (!baseDate) {
        baseDate = getSpecialConditionDueDate(
          specialConditions?.renderedConditions,
          condition.specialConditionId
        )
      }

      if (baseDate) {
        // If there are additional days to add
        if (
          condition.daysFromDate !== undefined &&
          condition.daysFromDate > 0
        ) {
          const calculatedDate = addTimeUnit(
            baseDate,
            condition.daysFromDate,
            condition.fromDateTimeUnit,
            condition.dayType === DayType.BusinessDays
          )
          return toDueDateResult(
            moveToNextBusinessDay(calculatedDate),
            isPending,
            isError
          )
        }

        if (referenceDate) {
          return toDateOnlyDueDateResult(referenceDate, isPending, isError)
        }

        return toDueDateResult(
          moveToNextBusinessDay(baseDate),
          isPending,
          isError
        )
      }
    }

    // If it's N days from a date (or AsPerContract which is equivalent)
    if (
      conditionValue === Condition.NdaysFromDate ||
      conditionValue === Condition.AsPerContract
    ) {
      const baseDate = getBaseDateFromConditionType(
        condition.conditionType as ConditionType,
        effectiveContractDates
      )

      if (!baseDate) {
        return toDueDateResult(null, isPending, isError)
      }

      // If daysFromDate is undefined or 0, use base date directly
      if (
        condition.daysFromDate === undefined ||
        condition.daysFromDate === 0
      ) {
        return toDueDateResult(
          moveToNextBusinessDay(baseDate),
          isPending,
          isError
        )
      }

      const isBusinessDays = condition.dayType === DayType.BusinessDays

      const calculatedDate = addTimeUnit(
        baseDate,
        condition.daysFromDate,
        condition.fromDateTimeUnit,
        isBusinessDays
      )

      return toDueDateResult(
        moveToNextBusinessDay(calculatedDate),
        isPending,
        isError
      )
    }

    // Handle deposits with conditionType but no condition value set
    // This covers cases where a deposit only has conditionType (e.g., "Contract") without an explicit condition
    if (conditionValue === undefined && condition.conditionType !== undefined) {
      const depositCondition = condition as Deposit
      const baseDate = getBaseDateFromConditionType(
        depositCondition.conditionType,
        effectiveContractDates
      )

      if (baseDate) {
        // Move to next business day if it falls on weekend or public holiday
        return toDueDateResult(
          moveToNextBusinessDay(baseDate),
          isPending,
          isError
        )
      }
    }

    return toDueDateResult(null, isPending, isError)
  }, [
    condition,
    isPending,
    isError,
    contractDates,
    contractDateOverride,
    specialConditions,
    addTimeUnit,
    addBusinessDays,
    campaign?.state,
    moveToNextBusinessDay,
  ])
}
